import type { Vec3 } from './math.js';
import { vec3 } from './math.js';

/**
 * Maps are brush-based: a list of axis-aligned boxes. This keeps collision
 * exact, cheap and trivially identical on both sides of the wire. Art passes
 * later can attach meshes to brushes without changing collision.
 */
export interface Brush {
  /** Center of the box. */
  center: Vec3;
  /** Half-extents on each axis. */
  half: Vec3;
  /** Material key — renderer only; every material collides identically. */
  material: MaterialKey;
}

export type MaterialKey = 'floor' | 'wall' | 'crate' | 'accent' | 'glass' | 'neon';

/** Per-map colours, so each map reads as a distinct place at a glance. */
export type Palette = Record<MaterialKey, number>;

/**
 * Lighting is part of the map, not the renderer. Greybox surfaces have no
 * texture detail to carry mood, so light colour and intensity are doing all the
 * work of making a map feel like a foundry rather than a desert courtyard.
 */
export interface MapLighting {
  /** Hemisphere light: sky tint above, bounce tint below. */
  skyColor: number;
  groundColor: number;
  hemisphereIntensity: number;
  sunColor: number;
  sunIntensity: number;
  sunPosition: Vec3;
  /** Flat fill so surfaces facing away from the sun never go black. */
  ambientColor: number;
  ambientIntensity: number;
  /** Tone-mapping exposure. The single knob for overall brightness. */
  exposure: number;
}

export interface SpawnPoint {
  position: Vec3;
  yaw: number;
  team: 0 | 1;
}

/**
 * A queue pad on the lobby floor. Stand on it to queue for a match: both sides
 * are circles, told apart by colour — blue ring vs red ring. Pads are flat
 * markers, not collision; the sim never sees them.
 */
export interface PadDef {
  /** Pads with the same group id form one game (e.g. both '1v1' pads). */
  group: string;
  team: 0 | 1;
  shape: 'square' | 'circle';
  x: number;
  z: number;
  /** Circle radius, or half the side length for squares. */
  radius: number;
  /** Players this side can hold (1 for 1v1, 2 for 2v2). */
  capacity: number;
  /**
   * Set on vs-bot pads: every human that queues here adds this many bots to
   * the enemy team. A group with this set has a single ring — the humans'
   * side — and the opposition is synthesized at launch.
   */
  botsPerPlayer?: number;
}

/** True when `(x, z)` is on the pad. */
export const padContains = (pad: PadDef, x: number, z: number): boolean => {
  const dx = x - pad.x;
  const dz = z - pad.z;
  return pad.shape === 'circle'
    ? dx * dx + dz * dz <= pad.radius * pad.radius
    : Math.abs(dx) <= pad.radius && Math.abs(dz) <= pad.radius;
};

export interface PadGroup {
  id: string;
  capacity: number;
  /** Indexed by team: [0] is the blue side, [1] the red side. */
  pads: [PadDef, PadDef];
  /**
   * True for single-ring groups (vs-bot pads): both entries of `pads` are the
   * same pad, only side 0 queues, and the opposition comes from
   * `botsPerPlayer` rather than a second ring.
   */
  solo: boolean;
  /** Bots per human at launch. Only set when `solo`. */
  botsPerPlayer?: number;
}

/**
 * Pads paired into games, in a stable order derived from the map data. The
 * server's queue logic and the client's UI both build this list from the same
 * map, which is what lets the wire refer to a group by index alone.
 */
export const padGroups = (map: GameMap): PadGroup[] => {
  const groups: PadGroup[] = [];
  for (const pad of map.pads) {
    let group = groups.find((entry) => entry.id === pad.group);
    if (!group) {
      group = {
        id: pad.group,
        capacity: pad.capacity,
        pads: [pad, pad],
        solo: true,
        ...(pad.botsPerPlayer !== undefined && { botsPerPlayer: pad.botsPerPlayer }),
      };
      groups.push(group);
      continue;
    }
    group.pads[pad.team] = pad;
    group.solo = false;
  }
  return groups;
};

export interface GameMap {
  id: string;
  name: string;
  /** One-line description, shown in the map picker. */
  tagline: string;
  /**
   * False for the lobby: weapons are dead and falling out of bounds teleports
   * you back instead of killing you. Both sides of the wire derive this from
   * the map itself, so prediction and authority can never disagree about it.
   */
  combat: boolean;
  /** Anything below this Y is out of bounds and kills the player. */
  killPlaneY: number;
  backgroundColor: number;
  fogColor: number;
  fogNear: number;
  fogFar: number;
  /** Half-width of the ground grid overlay, in metres. */
  gridExtent: number;
  lighting: MapLighting;
  palette: Palette;
  brushes: Brush[];
  spawns: SpawnPoint[];
  /** Queue pads. Empty everywhere except the lobby. */
  pads: PadDef[];
}

const box = (
  cx: number,
  cy: number,
  cz: number,
  hx: number,
  hy: number,
  hz: number,
  material: MaterialKey = 'wall',
): Brush => ({ center: vec3(cx, cy, cz), half: vec3(hx, hy, hz), material });

/**
 * Spawn facing a point rather than a raw angle — the yaw convention
 * (forward = -sin(yaw), -cos(yaw)) is easy to get backwards by hand.
 * `position` is the player's feet.
 */
const spawn = (
  x: number,
  y: number,
  z: number,
  team: 0 | 1,
  lookAtX = 0,
  lookAtZ = 0,
): SpawnPoint => ({
  position: vec3(x, y, z),
  yaw: Math.atan2(-(lookAtX - x), -(lookAtZ - z)),
  team,
});

/** Four walls around a square arena of the given half-extent. */
const perimeter = (
  half: number,
  height: number,
  material: MaterialKey = 'wall',
): Brush[] => [
  box(0, height / 2, -half, half, height / 2, 0.5, material),
  box(0, height / 2, half, half, height / 2, 0.5, material),
  box(-half, height / 2, 0, 0.5, height / 2, half, material),
  box(half, height / 2, 0, 0.5, height / 2, half, material),
];

/** Rise per step. Must stay below STEP_HEIGHT or players cannot walk up. */
const STEP_RISE = 0.45;

/**
 * A flight of steps, each a solid block from the ground up. Collision is
 * axis-aligned boxes only, so every slope in the game is a staircase — this
 * helper keeps them walkable by construction rather than by eyeballing heights.
 *
 * @param axis  direction of travel; the flight climbs as it advances
 * @param sign  +1 or -1 along that axis
 */
const stairs = (
  startX: number,
  startZ: number,
  axis: 'x' | 'z',
  sign: 1 | -1,
  steps: number,
  run: number,
  halfWidth: number,
  material: MaterialKey = 'crate',
): Brush[] =>
  Array.from({ length: steps }, (_, index) => {
    const height = (index + 1) * STEP_RISE;
    const offset = sign * (index * run + run / 2);
    return axis === 'z'
      ? box(
          startX,
          height / 2,
          startZ + offset,
          halfWidth,
          height / 2,
          run / 2,
          material,
        )
      : box(
          startX + offset,
          height / 2,
          startZ,
          run / 2,
          height / 2,
          halfWidth,
          material,
        );
  });

/** Top surface height of a flight produced by `stairs`. */
const stairsTop = (steps: number): number => steps * STEP_RISE;

// ---------------------------------------------------------------------------
// Warehouse — the original greybox arena. Cool industrial daylight.
// ---------------------------------------------------------------------------

export const WAREHOUSE: GameMap = {
  id: 'warehouse',
  name: 'Warehouse',
  tagline: 'Symmetric two-level box · mid tower · long side catwalks',
  combat: true,
  pads: [],
  killPlaneY: -20,
  backgroundColor: 0x8fa8c8,
  fogColor: 0x9db4d0,
  fogNear: 70,
  fogFar: 240,
  gridExtent: 40,
  lighting: {
    skyColor: 0xdce9ff,
    groundColor: 0x6d7183,
    hemisphereIntensity: 2.2,
    sunColor: 0xfff4e0,
    sunIntensity: 2.4,
    sunPosition: vec3(40, 80, 20),
    ambientColor: 0xb9c6da,
    ambientIntensity: 0.55,
    exposure: 1.15,
  },
  palette: {
    floor: 0x6f7889,
    wall: 0x8b95a8,
    crate: 0xc19a68,
    accent: 0x5aa4cf,
    glass: 0xbfe4ff,
    neon: 0x53d7ff,
  },
  brushes: [
    // Ground
    box(0, -0.5, 0, 40, 0.5, 40, 'floor'),
    ...perimeter(40, 9),

    // Mid tower
    box(0, 1.5, 0, 6, 1.5, 6, 'accent'),
    box(0, 3.25, 0, 4, 0.25, 4, 'accent'),
    box(-3, 0.75, -8, 3, 0.75, 2, 'crate'),
    box(3, 0.75, 8, 3, 0.75, 2, 'crate'),

    // Side catwalks, walkable at y = 3.25.
    box(-22, 3, 0, 6, 0.25, 18, 'accent'),
    box(22, 3, 0, 6, 0.25, 18, 'accent'),
    // Railings on the inner edge only, so the drop to mid stays open.
    box(-16.2, 3.6, 0, 0.3, 0.35, 18),
    box(16.2, 3.6, 0, 0.3, 0.35, 18),

    // Stairs up to each catwalk, climbing to just above its surface.
    ...stairs(-22, -27, 'z', 1, 8, 1.2, 3),
    ...stairs(22, 27, 'z', -1, 8, 1.2, 3),

    // Scattered cover
    box(-12, 1, 12, 2, 1, 2, 'crate'),
    box(12, 1, -12, 2, 1, 2, 'crate'),
    box(-12, 1, -20, 2, 1, 6, 'crate'),
    box(12, 1, 20, 2, 1, 6, 'crate'),
    box(-30, 1.5, 24, 4, 1.5, 4, 'crate'),
    box(30, 1.5, -24, 4, 1.5, 4, 'crate'),
    box(0, 1, -26, 8, 1, 1, 'crate'),
    box(0, 1, 26, 8, 1, 1, 'crate'),
  ],
  spawns: [
    spawn(-32, 0.5, -32, 0),
    spawn(-26, 0.5, -34, 0),
    spawn(-34, 0.5, -26, 0),
    spawn(-22, stairsTop(8) + 0.2, 10, 0),
    spawn(32, 0.5, 32, 1),
    spawn(26, 0.5, 34, 1),
    spawn(34, 0.5, 26, 1),
    spawn(22, stairsTop(8) + 0.2, -10, 1),
  ],
};

// ---------------------------------------------------------------------------
// Foundry — tight, vertical, warm. Rewards air control and close-range weapons.
// ---------------------------------------------------------------------------

export const FOUNDRY: GameMap = {
  id: 'foundry',
  name: 'Foundry',
  tagline: 'Tight three-storey shaft · heavy verticality · close quarters',
  combat: true,
  pads: [],
  killPlaneY: -20,
  backgroundColor: 0x3a2a24,
  fogColor: 0x4a342a,
  fogNear: 35,
  fogFar: 120,
  gridExtent: 26,
  lighting: {
    skyColor: 0xffd9a8,
    groundColor: 0x5a3a2c,
    hemisphereIntensity: 1.9,
    sunColor: 0xffb264,
    sunIntensity: 2.6,
    sunPosition: vec3(-20, 50, -30),
    ambientColor: 0xd8a077,
    ambientIntensity: 0.7,
    exposure: 1.25,
  },
  palette: {
    floor: 0x6b5b52,
    wall: 0x8a7266,
    crate: 0xb4784a,
    accent: 0xd98b3f,
    glass: 0xffd9a8,
    neon: 0xffb264,
  },
  brushes: [
    box(0, -0.5, 0, 26, 0.5, 26, 'floor'),
    ...perimeter(26, 14),

    // Central smelter block, climbable on three sides.
    box(0, 2, 0, 5, 2, 5, 'accent'),
    box(0, 4.25, 0, 3.5, 0.25, 3.5, 'accent'),
    box(0, 1, -7, 4, 1, 2, 'crate'),
    box(-7, 1, 0, 2, 1, 4, 'crate'),
    box(7, 1, 0, 2, 1, 4, 'crate'),

    // Upper gantry ring, reachable from the corner stairs.
    box(0, 6, -18, 26, 0.25, 3, 'accent'),
    box(0, 6, 18, 26, 0.25, 3, 'accent'),
    box(-18, 6, 0, 3, 0.25, 15, 'accent'),
    box(18, 6, 0, 3, 0.25, 15, 'accent'),
    // Railings, so players do not walk straight off the gantry.
    box(0, 6.75, -15, 26, 0.5, 0.3),
    box(0, 6.75, 15, 26, 0.5, 0.3),

    // Corner stairs to the gantry: real, walkable flights that hug the strip
    // between the wall and the gantry's outer edge and top out flush with its
    // deck. The blocks that used to sit here rose in +2/+3 jumps nobody could
    // climb, so the gantry was unreachable from the ground — and their gaps
    // against the walls formed corner pits you could drop into off the gantry
    // and never jump out of.
    ...stairs(-25.5, -23.25, 'x', 1, 14, 0.8, 2.25),
    ...stairs(25.5, 23.25, 'x', -1, 14, 0.8, 2.25),
    // Landings at the top of each flight. The stairs climb the strip *outside*
    // the gantry and only touch its edge, so cresting the last step used to
    // mean falling straight back to the floor unless you immediately turned
    // onto the deck. The landing continues the stair top and overlaps the
    // deck edge, so momentum carries you onto the gantry instead of into air.
    box(-11.3, stairsTop(14) - 0.25, -23, 3, 0.25, 2.5, 'accent'),
    box(11.3, stairsTop(14) - 0.25, 23, 3, 0.25, 2.5, 'accent'),
    // A rail across each landing's far end — cresting the stairs at sprint
    // speed otherwise carries you straight off the edge, which is the exact
    // fall this landing exists to prevent.
    box(-8.3, stairsTop(14) + 0.5, -23, 0.15, 0.5, 2.5),
    box(8.3, stairsTop(14) + 0.5, 23, 0.15, 0.5, 2.5),

    // Ladle pits — cover at ground level.
    box(-13, 1.5, 13, 3, 1.5, 3, 'crate'),
    box(13, 1.5, -13, 3, 1.5, 3, 'crate'),
    box(-13, 0.75, -6, 2, 0.75, 3, 'crate'),
    box(13, 0.75, 6, 2, 0.75, 3, 'crate'),
  ],
  spawns: [
    spawn(-21, 0.5, 8, 0),
    spawn(-21, 0.5, 2, 0),
    spawn(-19, 0.5, 14, 0),
    spawn(-18, 6.6, -18, 0),
    spawn(21, 0.5, -8, 1),
    spawn(21, 0.5, -2, 1),
    spawn(19, 0.5, -14, 1),
    spawn(18, 6.6, 18, 1),
  ],
};

// ---------------------------------------------------------------------------
// Sandstone — open, bright, long sightlines. The sniper map.
// ---------------------------------------------------------------------------

export const SANDSTONE: GameMap = {
  id: 'sandstone',
  name: 'Sandstone',
  tagline: 'Open desert plaza · long sightlines · pillar cover',
  combat: true,
  pads: [],
  killPlaneY: -20,
  backgroundColor: 0xc4d8ef,
  fogColor: 0xd9d0b8,
  fogNear: 90,
  fogFar: 320,
  gridExtent: 50,
  lighting: {
    skyColor: 0xfff6e2,
    groundColor: 0xbfa477,
    hemisphereIntensity: 2.6,
    sunColor: 0xfff1cf,
    sunIntensity: 3.0,
    sunPosition: vec3(60, 90, -40),
    ambientColor: 0xe8dcc0,
    ambientIntensity: 0.7,
    exposure: 1.1,
  },
  palette: {
    floor: 0xbda878,
    wall: 0xcfb98a,
    crate: 0xa8895c,
    accent: 0x8fb8a0,
    glass: 0xd8f0ff,
    neon: 0xaef2cf,
  },
  brushes: [
    box(0, -0.5, 0, 50, 0.5, 50, 'floor'),
    ...perimeter(50, 8),

    // Raised central plaza, ramped on two sides.
    box(0, 1, 0, 12, 1, 12, 'accent'),
    box(0, 0.5, -14, 5, 0.5, 2, 'accent'),
    box(0, 0.5, 14, 5, 0.5, 2, 'accent'),
    box(0, 2.5, 0, 4, 0.5, 4, 'accent'),

    // Pillar field — the only cover across the open ground.
    box(-24, 2, -24, 2, 2, 2, 'crate'),
    box(-24, 2, 0, 2, 2, 2, 'crate'),
    box(-24, 2, 24, 2, 2, 2, 'crate'),
    box(0, 2, -24, 2, 2, 2, 'crate'),
    box(0, 2, 24, 2, 2, 2, 'crate'),
    box(24, 2, -24, 2, 2, 2, 'crate'),
    box(24, 2, 0, 2, 2, 2, 'crate'),
    box(24, 2, 24, 2, 2, 2, 'crate'),

    // Low walls that break the longest diagonals.
    box(-16, 1, -8, 6, 1, 0.6),
    box(16, 1, 8, 6, 1, 0.6),
    box(-8, 1, 16, 0.6, 1, 6),
    box(8, 1, -16, 0.6, 1, 6),

    // Corner bastions — elevated, exposed, worth holding.
    box(-38, 2, -38, 6, 2, 6, 'crate'),
    box(-38, 3.5, -32, 6, 0.25, 1, 'accent'),
    box(38, 2, 38, 6, 2, 6, 'crate'),
    box(38, 3.5, 32, 6, 0.25, 1, 'accent'),
    box(-38, 1, 38, 5, 1, 5, 'crate'),
    box(38, 1, -38, 5, 1, 5, 'crate'),
  ],
  spawns: [
    spawn(-42, 0.5, -12, 0),
    spawn(-42, 0.5, 0, 0),
    spawn(-42, 0.5, 12, 0),
    spawn(-38, 4.5, -38, 0),
    spawn(42, 0.5, 12, 1),
    spawn(42, 0.5, 0, 1),
    spawn(42, 0.5, -12, 1),
    spawn(38, 4.5, 38, 1),
  ],
};

// ---------------------------------------------------------------------------
// Lobby — the walkable hub every player lands in. Not an arena: no weapons,
// no teams that matter, just other players and the queue pads. Warm evening
// light so it reads as a home base rather than a battleground.
// ---------------------------------------------------------------------------

export const LOBBY: GameMap = {
  id: 'lobby',
  name: 'Lobby',
  tagline: 'Walk onto a pad to queue — blue circle vs red circle',
  combat: false,
  killPlaneY: -10,
  backgroundColor: 0x9fc3e8,
  fogColor: 0xa8c4e0,
  fogNear: 45,
  fogFar: 150,
  gridExtent: 22,
  lighting: {
    skyColor: 0xeaf4ff,
    groundColor: 0x5a6377,
    hemisphereIntensity: 2.2,
    sunColor: 0xfff0d8,
    sunIntensity: 2.2,
    sunPosition: vec3(-25, 40, 20),
    ambientColor: 0xb9c4da,
    ambientIntensity: 0.6,
    exposure: 1.15,
  },
  palette: {
    floor: 0x8f98aa,
    wall: 0xaab3c4,
    crate: 0x8a6f52,
    accent: 0x3f8fd2,
    glass: 0xcfe8ff,
    neon: 0x35e0ff,
  },
  brushes: [
    // Plaza floor: 44 × 30.
    box(0, -0.5, 0, 22, 0.5, 15, 'floor'),
    box(0, 2.5, -15, 22, 2.5, 0.5),
    box(0, 2.5, 15, 22, 2.5, 0.5),
    box(-22, 2.5, 0, 0.5, 2.5, 15),
    box(22, 2.5, 0, 0.5, 2.5, 15),

    // A raised deck along the back of the spawn side, purely to hang out on.
    box(0, 0.25, 13, 12, 0.25, 2, 'accent'),
    ...stairs(-14, 12.4, 'x', 1, 1, 1.2, 2),

    // Planters splitting the walk from spawn to the pads.
    box(-14, 0.75, 2, 1.4, 0.75, 1.4, 'crate'),
    box(14, 0.75, 2, 1.4, 0.75, 1.4, 'crate'),
    box(0, 0.75, 4, 1.8, 0.75, 1.1, 'crate'),

    // Big sign panel behind the pads with a neon underline — the wall the whole
    // room points at, so the queue reads as the main event.
    box(0, 2.4, -14.7, 9, 1.4, 0.25, 'accent'),
    box(0, 0.95, -14.55, 10, 0.1, 0.12, 'neon'),
    box(0, 3.95, -14.55, 20, 0.08, 0.1, 'neon'),

    // Floor runway: a lit strip from spawn to the pad row, with a crossbar
    // linking the two groups. 4 cm tall, so it is walked over, not around.
    box(0.7, 0.01, 0, 0.25, 0.02, 10, 'neon'),
    box(0.7, 0.01, -9.5, 10, 0.02, 0.12, 'neon'),

    // Corner pillars with glowing caps, framing the room.
    box(-20.6, 2.5, -13.6, 0.6, 2.5, 0.6),
    box(20.6, 2.5, -13.6, 0.6, 2.5, 0.6),
    box(-20.6, 2.5, 13.6, 0.6, 2.5, 0.6),
    box(20.6, 2.5, 13.6, 0.6, 2.5, 0.6),
    box(-20.6, 5.06, -13.6, 0.7, 0.06, 0.7, 'neon'),
    box(20.6, 5.06, -13.6, 0.7, 0.06, 0.7, 'neon'),
    box(-20.6, 5.06, 13.6, 0.7, 0.06, 0.7, 'neon'),
    box(20.6, 5.06, 13.6, 0.7, 0.06, 0.7, 'neon'),

    // Display alcoves along the side walls — a nod to the shop fronts a real
    // hub would have. Wood counters with glass cases on top.
    box(-21, 1, -4, 0.8, 1, 3, 'crate'),
    box(-21, 2.6, -4, 0.7, 0.6, 2.8, 'glass'),
    box(21, 1, 4, 0.8, 1, 3, 'crate'),
    box(21, 2.6, 4, 0.7, 0.6, 2.8, 'glass'),
  ],
  // Flat visual markers only — the queue logic reads `pads`, not brushes.
  // The back row (z = -9.5) is player-vs-player; the front row (z = 5, on the
  // walk from spawn) is the vs-bot rooms: one ring each, every human who
  // queues adds ×N bots to the enemy team, difficulty voted on the pad.
  pads: [
    { group: '1v1', team: 0, shape: 'circle', x: -9, z: -9.5, radius: 1.3, capacity: 1 },
    { group: '1v1', team: 1, shape: 'circle', x: -4.2, z: -9.5, radius: 1.3, capacity: 1 },
    { group: '2v2', team: 0, shape: 'circle', x: 4.6, z: -9.5, radius: 2.0, capacity: 2 },
    { group: '2v2', team: 1, shape: 'circle', x: 10.4, z: -9.5, radius: 2.0, capacity: 2 },
    { group: 'bots ×1', team: 0, shape: 'circle', x: -16, z: 5, radius: 1.6, capacity: 4, botsPerPlayer: 1 },
    { group: 'bots ×2', team: 0, shape: 'circle', x: -8, z: 5, radius: 1.6, capacity: 4, botsPerPlayer: 2 },
    { group: 'bots ×3', team: 0, shape: 'circle', x: 8, z: 5, radius: 1.6, capacity: 4, botsPerPlayer: 3 },
    { group: 'bots ×4', team: 0, shape: 'circle', x: 16, z: 5, radius: 1.6, capacity: 4, botsPerPlayer: 4 },
  ],
  // Teams are meaningless in the lobby; both exist only so the sim can spawn
  // anyone. Everyone faces the pads.
  spawns: [
    spawn(-8, 0.5, 9, 0, -9, -9.5),
    spawn(-4, 0.5, 10, 0, -4.2, -9.5),
    spawn(0, 0.5, 9, 0, 0, -9.5),
    spawn(8, 0.5, 9, 1, 10.4, -9.5),
    spawn(4, 0.5, 10, 1, 4.6, -9.5),
    spawn(-1, 0.5, 9.5, 1, 0, -9.5),
  ],
};

export const HUB_MAP_ID = LOBBY.id;

export const MAPS: Record<string, GameMap> = {
  [WAREHOUSE.id]: WAREHOUSE,
  [FOUNDRY.id]: FOUNDRY,
  [SANDSTONE.id]: SANDSTONE,
  [LOBBY.id]: LOBBY,
};

/**
 * The arena pool: what pad votes choose between. The lobby is deliberately not
 * in here — you can stand in it, but never fight on it.
 */
export const MAP_ROTATION: readonly string[] = [WAREHOUSE.id, FOUNDRY.id, SANDSTONE.id];

export const DEFAULT_MAP_ID = WAREHOUSE.id;

export const getMap = (id: string): GameMap => MAPS[id] ?? WAREHOUSE;

export const isMapId = (id: string): boolean => id in MAPS;

/** The map after `id` in the rotation, wrapping around. */
export const nextMapId = (id: string): string => {
  const index = MAP_ROTATION.indexOf(id);
  return MAP_ROTATION[(index + 1) % MAP_ROTATION.length] ?? DEFAULT_MAP_ID;
};
